import { useRouter } from "next/router";
import Link from "next/link";
import styles from "./Breadcrumbs.module.css";
import { Constants } from "@/schemas/data";
import Head from "next/head";
import Schemas from "@/schemas";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { fetchCommonData } from "@/services/CommonService";
import { fetchMegamenuData } from "@/services/CmsService";
import { ChevronRight } from "lucide-react";
import {
  getLocaleFromPath,
  detectCountryFromPath,
  KNOWN_COUNTRIES,
  getLocaleFromCountry,
  ensureLocalesLoaded,
} from "@/Config/countryConstants";
import { getSelectedCountryByUser } from "@/utils/cookieutils";

interface BreadcrumbsProps {
  classValue?: string;
  hideInnerBox?: boolean; // when true, hide the inner breadcrumb container (used on 404 page)
  leftAlignDropdown?: boolean; // when true, force dropdown text alignment to left (scoped use e.g., 404)
}

type BreadcrumbItem = { title: string; href?: string };

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  classValue,
  hideInnerBox,
  leftAlignDropdown,
}) => {
  const router = useRouter();
  const { asPath } = router;

  // Remove query parameters from the path
  const pathWithoutQuery = asPath.split("?")[0];
  const pathArray = pathWithoutQuery.split("/").filter((path) => path);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [headerData, setHeaderData] = useState<any>();
  const [megamenuData, setMegamenuData] = useState<any>();

  // Helper must be declared before use
  const capitalizeText = (text: string) => {
    const cleanedText = (text || "").replace(/[\/\-]/g, " ").toLowerCase();
    return cleanedText
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const currentCountry = detectCountryFromPath(pathWithoutQuery || "/");
  // Prefer cookie-selected country on global pages (no country in URL)
  const cookieCountry = getSelectedCountryByUser();
  const effectiveCountry =
    currentCountry ||
    (cookieCountry && KNOWN_COUNTRIES?.includes(cookieCountry)
      ? cookieCountry
      : null);
  const baseCrumb = effectiveCountry
    ? { title: capitalizeText(effectiveCountry), href: `/${effectiveCountry}` }
    : { title: "Home", href: "/" };

  const [loading, setLoading] = useState<boolean>(true);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await ensureLocalesLoaded();
        const pathLocale = getLocaleFromPath(pathWithoutQuery || "/");
        const cookieCountryLocal = getSelectedCountryByUser();
        const effectiveLocale =
          cookieCountryLocal && cookieCountryLocal !== "south-africa"
            ? getLocaleFromCountry(cookieCountryLocal)
            : pathLocale;
        const [commonResponse, megamenuResponse] = await Promise.all([
          fetchCommonData(effectiveLocale),
          fetchMegamenuData(effectiveLocale),
        ]);
        setHeaderData(commonResponse?.data?.header || null);
        setMegamenuData(megamenuResponse?.data || megamenuResponse || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathWithoutQuery]);

  useEffect(() => {
    // Close dropdown if clicked outside
    if (typeof window !== "undefined") {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setDropdownVisible(false);
        }
      };

      // Add event listener on component mount
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup on component unmount
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, []);

  if (loading) return <div>Loading...</div>;
  // Do not block rendering on error; fall back to path-based breadcrumbs

  // Use megamenu data for program navigation, fallback to header data
  const coursesData =
    megamenuData?.mega_menu_header?.main_nav_links ||
    headerData?.main_nav_links?.[0]?.nested_list_links ||
    [];
  const findBreadcrumbs = (slug: string): BreadcrumbItem[] => {
    const customTitles: { [key: string]: string } = {
      "contact-us-for-admissions-and-other-queries-regenesys": "Contact",
      "student-loans": "Get Student Funding",
      "all-academic-programmes": "All Programmes",
      "leadership-team-and-faculty": "Leadership",
    };

    // Check if the slug has a custom title
    if (customTitles[slug]) {
      return [baseCrumb, { title: customTitles[slug] }];
    }

    // Explicit handling for Events breadcrumbs
    // Patterns:
    // - /events -> Home > Events
    // - /events/[slug] -> Home > Events > [Event Title]
    // - /{country}/events -> {Country} > Events
    // - /{country}/events/[slug] -> {Country} > Events > [Event Title]
    const segments = pathArray;
    if (Array.isArray(segments) && segments.length > 0) {
      // Global events listing
      if (segments.length === 1 && segments[0] === "events") {
        return [baseCrumb, { title: "Events" }];
      }

      // Global event details: /events/[slug]
      if (segments.length >= 2 && segments[0] === "events") {
        return [
          baseCrumb,
          { title: "Events", href: "/events" },
          { title: capitalizeText(slug) },
        ];
      }

      // Country-scoped events listing: /{country}/events
      if (
        currentCountry &&
        segments.length === 2 &&
        segments[0] === currentCountry &&
        segments[1] === "events"
      ) {
        return [baseCrumb, { title: "Events" }];
      }

      // Country-scoped event details: /{country}/events/[slug]
      if (
        currentCountry &&
        segments.length >= 3 &&
        segments[0] === currentCountry &&
        segments[1] === "events"
      ) {
        return [
          baseCrumb,
          { title: "Events", href: `/${currentCountry}/events` },
          { title: capitalizeText(slug) },
        ];
      }
    }

    // Loop through the courses data to find a match
    for (const school of coursesData) {
      // Check if the slug matches the school URL
      if (school?.url === slug) {
        return [
          baseCrumb,
          { title: "All Academic Programme", href: "/all-academic-programmes" },
          { title: capitalizeText(school?.title || "") },
        ];
      }

      // Check if the slug matches any section URL within the school (megamenu structure)
      for (const section of school?.sections || []) {
        if (section?.url === slug) {
          return [
            baseCrumb,
            {
              title: "All Academic Programme",
              href: "/all-academic-programmes",
            },
            {
              title: capitalizeText(school?.title || ""),
              href: `/${school?.url || ""}`,
            },
            { title: capitalizeText(section?.title || "") },
          ];
        }

        // Check if the slug matches any item URL within the section
        for (const item of section?.items || []) {
          // Normalize URLs to compare with/without country prefix
          const itemUrlNorm = (item?.url || "").replace(/^\/+/, "");
          const sectionUrlNorm = (section?.url || "").replace(/^\/+/, "");
          const schoolUrlNorm = (school?.url || "").replace(/^\/+/, "");
          const slugNorm = (slug || "").replace(/^\/+/, "");

          const matchesItem =
            itemUrlNorm === slugNorm || itemUrlNorm.endsWith(`/${slugNorm}`); // handles country-prefixed item URLs like 'kenya/doctor-of-business-management'

          if (matchesItem) {
            return [
              baseCrumb,
              {
                title: "All Academic Programme",
                href: "/all-academic-programmes",
              },
              {
                title: capitalizeText(school?.title || ""),
                href: schoolUrlNorm ? `/${schoolUrlNorm}` : undefined,
              },
              {
                title: capitalizeText(section?.title || sectionUrlNorm || ""),
                href: sectionUrlNorm ? `/${sectionUrlNorm}` : undefined,
              },
              { title: capitalizeText(item?.label || slugNorm) },
            ];
          }
        }
      }

      // Check if the slug matches any list URL within the school (fallback for old structure)
      for (const list of school?.lists || []) {
        if (list?.url === slug) {
          return [
            baseCrumb,
            {
              title: "All Academic Programme",
              href: "/all-academic-programmes",
            },
            {
              title: capitalizeText(school?.title || ""),
              href: `/${school?.url || ""}`,
            },
            { title: capitalizeText(list?.url || "") },
          ];
        }

        // Check if the slug matches any item URL within the list
        for (const item of list?.items || []) {
          if (item?.url === slug) {
            return [
              baseCrumb,
              {
                title: "All Academic Programme",
                href: "/all-academic-programmes",
              },
              {
                title: capitalizeText(school?.title || ""),
                href: `/${school?.url || ""}`,
              },
              {
                title: capitalizeText(list?.url || ""),
                href: `/${list?.url || ""}`,
              },
              { title: capitalizeText(item?.label || "") },
            ];
          }
        }
      }

      // Check if the slug matches any link within the school
      for (const item of school?.links || []) {
        if (item?.url === slug) {
          return [
            baseCrumb,
            {
              title: "All Academic Programme",
              href: "/all-academic-programmes",
            },
            {
              title: capitalizeText(school?.title || ""),
              href: `/${school?.url || ""}`,
            },
            { title: capitalizeText(item?.label || "") },
          ];
        }
      }
    }

    // Fallback for unmatched slugs
    return [baseCrumb, { title: capitalizeText(slug) }];
  };

  const handleToggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const breadcrumbData = findBreadcrumbs(pathArray[pathArray.length - 1]);
  // Get the first and last items

  const firstItem = breadcrumbData?.[0] || { title: "Home", href: "/" };
  const lastItem = breadcrumbData?.[breadcrumbData.length - 1];

  // Get the middle items (those that should be hidden by default)
  const middleItems = breadcrumbData?.slice(1, breadcrumbData.length - 1);

  return (
    <>
      <Head>
        <Schemas type={Constants.breadCrumb} data={breadcrumbData} />
      </Head>
      {/* <div
        className={
          classValue === "boxStyle" ? styles.boxStyle : styles.breadcrumbSpacing
        }
      >
        {breadcrumbData?.map((item, index) => (
          <span key={index} className={styles.link}>
            {item.href ? (
              <Link href={item.href}>{item.title}</Link>
            ) : (
              <span className={styles.lastItem}>{item.title}</span>
            )}
            {index < breadcrumbData?.length - 1 && " /"}
          </span>
        ))}
      </div> */}

      <div
        className={
          classValue === "boxStyle" ? styles.boxStyle : styles.breadcrumbSpacing
        }
      >
        <div className="container">
          {!hideInnerBox && (
            <div className={styles.breadCrumbInnerBox}>
              {/* Render the first breadcrumb */}
              <div className={styles.link}>
                <Link title={firstItem?.title} href={firstItem?.href || "#"}>
                  {firstItem?.title}
                </Link>
                {breadcrumbData?.length === 2 ? <ChevronRight /> : ""}
              </div>

              {/* Show ellipsis if there are middle items */}
              {breadcrumbData?.length > 2 && (
                <span
                  className={styles.ellipsis}
                  onClick={handleToggleDropdown}
                >
                  <ChevronRight />
                  <span>
                    <Image
                      src="/assets/moreBreadcrumbs.png"
                      alt="more-breadcrumbs"
                      title="more-breadcrumbs"
                      width={32}
                      height={32}
                    />
                  </span>
                  <ChevronRight />
                </span>
              )}

              {/* Render dropdown with middle items if dropdown is visible */}
              {dropdownVisible &&
                middleItems?.filter((i) => i?.title)?.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className={styles.dropdown}
                    style={
                      leftAlignDropdown ? { textAlign: "left" } : undefined
                    }
                  >
                    {middleItems
                      .filter((item) => item?.title)
                      .map((item, index) => (
                        <div
                          key={index}
                          className={styles.dropdownItem}
                          style={
                            leftAlignDropdown
                              ? { textAlign: "left" }
                              : undefined
                          }
                        >
                          {"href" in item && item.href ? (
                            <Link title={item.title} href={item.href}>
                              {item.title}
                            </Link>
                          ) : (
                            <span className={styles.lastItem}>
                              {item.title}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                )}

              {/* Render the last breadcrumb */}
              {breadcrumbData?.length > 1 && (
                <span className={styles.link}>{lastItem?.title}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Breadcrumbs;
